import { fileToArrayBuffer } from '@/utils/file-to-arraybuffer';
import { sanitizeFilename } from '@/utils/sanitize-filename';
import { describeError } from '@/utils/describe-error';
import { PDFDocument } from 'pdf-lib';
import React, { useCallback, useRef, useState } from 'react';
import { UploadCloudIcon } from '../../icons/upload-cloud';
import styles from './pdf-drop-zone.module.scss';

type PdfDropzoneProps = {
	disabled?: boolean;
	className?: string;
	label?: string;
	hint?: string;
	accept?: string;
	maxSizeBytes?: number;
	onFileSelected?: (file: File) => void;
};

export const VALID_NAME_REGEX = /^[^.]+\.pdf$/i;

export default function PdfDropzone(props: PdfDropzoneProps) {
	const [isDragging, setIsDragging] = useState(false);
	const [documentError, setDocumentError] = useState<{
		title: string;
		message: string;
	} | null>(null);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const zoneRef = useRef<HTMLDivElement | null>(null);

	const openDocumentPicker = useCallback(() => {
		if (!props.disabled) inputRef.current?.click();
	}, [props.disabled]);

	const validateAndSet = useCallback(
		async (file?: File) => {
			if (!file) return;
			setDocumentError(null);
			const fileToProcess = new File(
				[file],
				sanitizeFilename(file.name),
				{ type: file.type },
			);

			const isPdfMime = fileToProcess.type === 'application/pdf';
			const isPdfExt = /\.pdf$/i.test(fileToProcess.name);
			if (!isPdfMime && !isPdfExt) {
				setDocumentError({
					title: 'No podemos cargar este archivo',
					message: 'Solo se aceptan archivos .PDF',
				});
				return;
			}
			if (!VALID_NAME_REGEX.test(fileToProcess.name)) {
				setDocumentError({
					title: 'No podemos cargar este archivo',
					message: 'El nombre el archivo es inválido',
				});
				return;
			}
			if (
				fileToProcess.size >
				(props.maxSizeBytes ?? Number.POSITIVE_INFINITY)
			) {
				setDocumentError({
					title: 'No podemos cargar este archivo',
					message: 'El archivo pesa más de 10MB',
				});
				return;
			}
			try {
				const arrayBuffer = await fileToArrayBuffer(fileToProcess);
				if (arrayBuffer) {
					await PDFDocument.load(arrayBuffer);
				} else {
					setDocumentError({
						title: 'No podemos cargar este archivo',
						message:
							'El archivo no es legible, prueba con uno distinto',
					});
					return;
				}
			} catch (error) {
				if (
					error instanceof Error &&
					error.message.includes('encrypted')
				) {
					setDocumentError({
						title: 'Este archivo está cifrado',
						message:
							'No pudimos abrir este archivo por sus configuraciones de seguridad. Comunícate con el autor para resolver esta situación',
					});
				} else {
					// Any other read or parse failure used to be swallowed, leaving the
					// user with no feedback; show the cause so it can be reported.
					setDocumentError({
						title: 'No podemos cargar este archivo',
						message: `No pudimos leer el PDF (${describeError(error)})`,
					});
				}
				return;
			}

			setDocumentError(null);
			props.onFileSelected?.(fileToProcess);
		},
		[props.onFileSelected],
	);

	const onInputChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			await validateAndSet(file);
			// reset input so selecting the same file again still triggers onChange
			e.target.value = '';
		},
		[validateAndSet],
	);

	const prevent = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const onDragEnter = (e: React.DragEvent) => {
		prevent(e);
		if (!props.disabled) setIsDragging(true);
	};

	const onDragOver = (e: React.DragEvent) => {
		prevent(e);
		if (!props.disabled) setIsDragging(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		prevent(e);
		setIsDragging(false);
	};

	const onDrop = (e: React.DragEvent) => {
		prevent(e);
		setIsDragging(false);
		if (props.disabled) return;

		const file = e.dataTransfer.files?.[0];
		validateAndSet(file);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (props.disabled) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			openDocumentPicker();
		}
	};

	return (
		<div
			className={`${styles['pdf-drop-zone-wrapper']} $ ${props.className}`}
		>
			<div
				ref={zoneRef}
				role="button"
				tabIndex={props.disabled ? -1 : 0}
				aria-disabled={props.disabled}
				aria-label="PDF Upload"
				onClick={openDocumentPicker}
				onKeyDown={onKeyDown}
				onDragEnter={onDragEnter}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				className={[
					styles['pdf-drop-zone'],
					isDragging ? styles['is-dragging'] : '',
					documentError ? styles['error'] : '',
				].join(' ')}
			>
				<UploadCloudIcon />
				<div className={styles['text']}>
					<div className={styles['label']}>{props.label}</div>
					<div className={styles['hint']}>{props.hint}</div>
				</div>

				{documentError && (
					<div className={styles['error-message']}>
						<div className={styles['title']}>
							{documentError.title}
						</div>
						<div className={styles['message']}>
							{documentError.message}
						</div>
					</div>
				)}

				<input
					ref={inputRef}
					type="file"
					accept={props.accept}
					onChange={onInputChange}
					style={{
						position: 'absolute',
						width: 1,
						height: 1,
						opacity: 0,
					}}
					tabIndex={-1}
					disabled={props.disabled}
				/>
			</div>
		</div>
	);
}
